/*
  Project header + tab bar numbers (UI overhaul wave 2, screen 04 ideas 1 and 2). Pure functions, no React.

  The header and the tab counts render before any tab has loaded its own records: they read the
  `overview` the API sends with the project (GET /projects/{id} → overview: boq_lines, variations,
  invoicing) and switch to the live records once a tab has loaded them, so an edit made on the page is
  reflected at once.

  buildHeaderFacts(...)  → contract value, invoiced, stages, execution, handover, team
  buildTabCounts(...)    → { stages: '2/9', material: 8, invoicing: 10, structure: 9, execution: 14, variations: 4 }
  filesAttention(...)    → why the Files tab needs a look (amber dot), or null
  TAB_RESOURCES          → which records each tab needs (the page loads them when the tab opens)
  canReadResource(...)   → whether the user's role may read a record set (skipped, not requested, when not)
*/
import { toNumber } from '@/lib/format';
import { hasPermission } from '@/lib/permissions';
import { buildStagePipeline, getHandoverState, isStageDone } from './projectDetailModel';

const num = (value) => {
  const parsed = toNumber(value);
  return parsed === null ? 0 : parsed;
};
const text = (value) => String(value ?? '').trim();

/** Records each tab needs. Tabs not listed here need only the project itself. */
export const TAB_RESOURCES = {
  stages: ['tasks'],
  material: ['boq', 'deliveryNotes', 'siteRequests', 'variations', 'tasks'],
  files: [],
  invoicing: ['invoices', 'variationInvoices', 'variations', 'tasks', 'qbStatus'],
  // Structure / Execution / Timeline / Variations (project-detail-b) read the project document; they also get the
  // BOQ (device order) and the variation records, so nothing they show waits on another tab.
  structure: ['boq'],
  execution: ['boq'],
  // 10 #4: the milestones lane shows invoices and VO approvals (skipped for roles without them)
  timeline: ['invoices', 'variations'],
  variations: ['variations', 'variationInvoices', 'boq', 'invoices', 'deliveryNotes'],
};

/** Resources a tab must have before its content renders (the rest load in the background). */
export const TAB_BLOCKING_RESOURCES = {
  material: ['boq', 'deliveryNotes', 'siteRequests', 'variations'],
  invoicing: ['invoices', 'variationInvoices', 'variations'],
  variations: ['variations', 'variationInvoices', 'boq'],
};

/**
 * The permission each record set's list endpoint checks (backend require_permission). A role without it is
 * never sent to that endpoint: the set is 'skipped' (empty, no 403, no "failed to load" toast). Sets not
 * listed here (users, field options, QuickBooks status) are optional and fail quietly.
 */
export const RESOURCE_PERMISSIONS = {
  boq: 'material',
  deliveryNotes: 'material',
  siteRequests: 'material',
  variations: 'variations',
  tasks: 'tasks',
  invoices: 'invoicing',
  variationInvoices: 'invoicing',
};

export const canReadResource = (key, user) => !RESOURCE_PERMISSIONS[key] || hasPermission(user, RESOURCE_PERMISSIONS[key]);

/**
 * true when every key is settled: 'ready', 'error' (a failed load still shows the tab with what it has) or
 * 'skipped' (the role may not read it).
 */
export const resourcesSettled = (status = {}, keys = []) => keys.every((key) => ['ready', 'error', 'skipped'].includes(status[key]));

/** Same rule as the Invoicing money bar: base invoices + invoices of approved VOs; "Not Started" = scheduled. */
export function summarizeInvoiced({ invoices = [], variationInvoices = [], variations = [] } = {}) {
  const approved = new Set((variations || []).filter((variation) => variation.status === 'Approved').map((variation) => variation.id));
  const counted = [...(invoices || []), ...(variationInvoices || []).filter((invoice) => approved.has(invoice.variation_id))];
  const issued = counted.filter((invoice) => (text(invoice.status) || 'Not Started') !== 'Not Started');
  const invoiced = issued.reduce((sum, invoice) => sum + num(invoice.amount), 0);
  const received = issued.filter((invoice) => invoice.status === 'Payment Received').reduce((sum, invoice) => sum + num(invoice.amount), 0);
  return { issued: issued.length, invoiced, received, pending: Math.max(0, invoiced - received) };
}

const approvedVariationValue = (variations = []) => (variations || [])
  .filter((variation) => variation.status === 'Approved')
  .reduce((sum, variation) => sum + num(variation.total_value ?? variation.financial_impact), 0);

/** Stage numbers for the header and the Stages tab: { approved, total, percent, next: stage | null } */
export function summarizeStages(stages = []) {
  const steps = buildStagePipeline(stages);
  const approved = (stages || []).filter((stage) => isStageDone(stage.status)).length;
  const total = (stages || []).length;
  const nextStep = steps.find((step) => step.state === 'current') || null;
  return {
    approved,
    total,
    percent: total ? Math.round((approved / total) * 100) : 0,
    next: nextStep ? (stages || []).find((stage) => stage.id === nextStep.id) || null : null,
    nextNumber: nextStep?.number || '',
  };
}

/**
 * Everything the header's key facts need.
 * data = { boqItems, variations, invoices, variationInvoices, loaded: { boq, variations, invoices, variationInvoices } }
 * perms = { viewValue, invoicing } — viewValue is the Invoicing permission (owner rule 13 Oct 2026: money only for
 * roles with Invoicing). A project response without its value fields (the server strips them for other roles)
 * gives no contract fact either, never "AED 0".
 */
export function buildHeaderFacts({ project, overview, data = {}, perms = {}, stages, execution, now } = {}) {
  const loaded = data.loaded || {};
  const base = num(project?.original_scope_value ?? project?.total_project_value);
  const variationsLoaded = Boolean(loaded.variations);
  // Roles with invoicing but without variations get only the approved VO total, inside overview.invoicing.
  const variationsValue = variationsLoaded
    ? approvedVariationValue(data.variations)
    : num(overview?.variations?.approved_value ?? overview?.invoicing?.approved_variations_value);
  const variationCount = variationsLoaded
    ? (data.variations || []).filter((variation) => variation.status === 'Approved').length
    : num(overview?.variations?.approved);
  const hasValue = [project?.original_scope_value, project?.total_project_value].some((value) => value !== null && value !== undefined && value !== '');
  const contract = perms.viewValue && hasValue ? { base, variations: variationsValue, variationCount, total: base + variationsValue } : null;

  let invoiced = null;
  if (perms.viewValue && perms.invoicing) {
    const live = loaded.invoices && loaded.variationInvoices && loaded.variations;
    const totals = live
      ? summarizeInvoiced({ invoices: data.invoices, variationInvoices: data.variationInvoices, variations: data.variations })
      : (overview?.invoicing ? {
        issued: num(overview.invoicing.issued),
        invoiced: num(overview.invoicing.invoiced),
        received: num(overview.invoicing.received),
        pending: num(overview.invoicing.pending),
      } : null);
    if (totals) {
      const contractTotal = contract?.total || 0;
      invoiced = { ...totals, percent: contractTotal > 0 ? Math.round((totals.invoiced / contractTotal) * 100) : 0 };
    }
  }

  const handoverState = getHandoverState(project, { now });
  return {
    contract,
    invoiced,
    stages,
    execution,
    handover: project?.end_date || project?.start_date
      ? { ...(handoverState || {}), endDate: project?.end_date || '', startDate: project?.start_date || '' }
      : null,
    team: [
      text(project?.project_engineer) && { name: text(project.project_engineer), role: 'PE' },
      text(project?.senior_project_engineer) && { name: text(project.senior_project_engineer), role: 'Senior PE' },
    ].filter(Boolean),
  };
}

/** Tab counts: live records once a tab loaded them, else the project overview. undefined = not shown. */
export function buildTabCounts({ overview, data = {}, stages, executionMainCount, structureBuildings } = {}) {
  const loaded = data.loaded || {};
  let invoicing;
  if (loaded.invoices && loaded.variationInvoices) invoicing = (data.invoices || []).length + (data.variationInvoices || []).length;
  else if (overview?.invoicing) invoicing = num(overview.invoicing.invoices) + num(overview.invoicing.variation_invoices);
  let material;
  if (loaded.boq) material = (data.boqItems || []).length;
  else if (overview && Object.prototype.hasOwnProperty.call(overview, 'boq_lines')) material = num(overview.boq_lines);
  let variations;
  if (loaded.variations) variations = (data.variations || []).length;
  else if (overview?.variations) variations = num(overview.variations.count);
  return {
    stages: stages?.total ? `${stages.approved}/${stages.total}` : undefined,
    material,
    invoicing,
    structure: structureBuildings || undefined,
    execution: executionMainCount || undefined,
    variations,
  };
}

/** The 25 required folders that need a look after a scan (Empty or Missing). */
export function countFoldersNeedingAttention(nodes = []) {
  let count = 0;
  const visit = (node) => {
    if (!node || typeof node !== 'object') return;
    if (node.status === 'missing' || node.is_empty || node.exists === false) count += 1;
    (node.children || []).forEach(visit);
  };
  (nodes || []).forEach(visit);
  return count;
}

/** One OneDrive folder written two ways ('Projects\\LIV\\' / 'projects/LIV') gives the same key (= backend folder_key). */
export const folderPathKey = (value) => text(value).replace(/^"+|"+$/g, '').replace(/\\/g, '/').split('/')
  .map((segment) => segment.trim()).filter(Boolean).join('/').toLowerCase();

/** A stored scan describes the project folder only while it is still the project folder (no root → assumed current). */
export const scanMatchesFolder = (scan, folderPath) => Boolean(scan)
  && (!text(scan.root_path) || folderPathKey(scan.root_path) === folderPathKey(folderPath));

/**
 * Why the Files tab shows an amber dot, or null. onedriveOn false = OneDrive is not set up on this server:
 * nobody on the project can connect it, so "OneDrive not connected" is no reason for a dot then.
 */
export function filesAttention({ project, audit, onedriveOn = true } = {}) {
  if (!text(project?.files_root_path)) return 'Project folder not set';
  if (onedriveOn && !project?.onedrive_status?.connected) return 'OneDrive not connected';
  if (audit?.structure) {
    const count = countFoldersNeedingAttention(audit.structure);
    if (count) return `${count} folder${count === 1 ? '' : 's'} need${count === 1 ? 's' : ''} attention`;
    return null;
  }
  // 06 #4 (wave 3): the last stored scan (manual or nightly), so the dot shows without scanning again —
  // only while it is a scan of the current project folder.
  const stored = project?.files_scan_summary;
  if (stored && stored.scanned_at && scanMatchesFolder(stored, project.files_root_path)) {
    const count = Number(stored.empty || 0) + Number(stored.missing || 0);
    if (count) return `${count} folder${count === 1 ? '' : 's'} need${count === 1 ? 's' : ''} attention`;
  }
  return null;
}
